import { Flex, Icon, Image, Input, Text, InputRightElement, IconButton, InputGroup } from '@chakra-ui/react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { BiImageAdd } from 'react-icons/bi';
import FeatherIcon from '@ailibs/feather-react-ts';
import { downloadFile, isFile } from '@/utils';
import { useTranslation } from 'react-i18next';

type ImageUploadProps = {
  imageURL: string;
  value?: File;
  onFileChange?: (file: File | undefined, onReset?: () => void) => void;
  label?: string;
  inputLabel?: string;
  helpText?: string;
  error?: string;
  previewWidth?: string;
  previewHeight?: string;
  withDownload?: boolean;
  accept?: string;
  disabled?: boolean;
};

export const ImageUpload = forwardRef<HTMLInputElement, ImageUploadProps>((props, ref) => {
  const { t } = useTranslation();

  const {
    imageURL,
    value,
    onFileChange,
    label,
    inputLabel,
    helpText = t('components.image_upload.image_upload_help_text'),
    error,
    previewWidth = '100px',
    previewHeight = '100px',
    withDownload,
    accept = '.png, .jpg, .jpeg, .svg',
    disabled,
  } = props;

  const innerRef = useRef<HTMLInputElement>(null);

  const ForwardedFeatherIcon = forwardRef<HTMLDivElement, React.ComponentProps<typeof FeatherIcon>>((props, ref) => (
    <div ref={ref}>
      <FeatherIcon {...props} />
    </div>
  ));

  const [file, setFile] = useState<File | undefined>(undefined);

  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

  const onHandleFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event?.target?.files?.[0];
      setFile(file);
      onFileChange?.(file, () => setFile(undefined));
    },
    [onFileChange],
  );

  const imageURLName = imageURL ? imageURL.split('/').pop() : '';
  const imageName = file ? file.name : imageURLName;

  useEffect(() => {
    if (!value || !isFile(value) || file) return;

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(value);
    setFile(value);

    if (innerRef.current) innerRef.current.files = dataTransfer.files;
  }, [file, value]);

  return (
    <InputGroup display='flex' flexDirection='column' width='100%'>
      {label ? (
        <Text fontWeight='medium' mb={2}>
          {label}
        </Text>
      ) : null}

      <Flex width='100%' gap={6}>
        <Image
          src={file ? URL.createObjectURL(file) : imageURL ? `${imageURL}?${new Date().getTime()}` : ''}
          borderWidth={file || imageURL ? '' : error ? '2px' : '1px'}
          borderStyle='solid'
          borderColor={error ? 'red.500' : 'black'}
          borderRadius='8px'
          minW={previewWidth}
          maxW={previewWidth}
          minH={previewHeight}
          maxH={previewHeight}
          objectFit='cover'
          overflow='hidden'
        />

        <Flex alignItems='center' gap={6} width='100%'>
          <Flex position='relative' width='100%' direction='column' gap={2}>
            {!disabled && <Input type='file' display='none' ref={innerRef} accept={accept} onChange={onHandleFile} />}{' '}
            <Flex direction='column' gap={1}>
              {inputLabel ? (
                <Text fontSize='13px' fontWeight='medium'>
                  {inputLabel}
                </Text>
              ) : null}

              {!disabled && (
                <Input
                  variant='flushed'
                  width='100%'
                  value={imageName}
                  noOfLines={1}
                  readOnly
                  cursor='pointer'
                  borderColor={error ? 'red.500' : undefined}
                  borderBottomWidth={error ? '2px' : undefined}
                  onClick={() => innerRef?.current?.click()}
                  fontSize={12}
                  pr={9}
                />
              )}
            </Flex>
            {!disabled && (
              <InputRightElement top='0' cursor='pointer' onClick={() => innerRef?.current?.click()}>
                <Icon viewBox='0 0 15 15' color={error ? 'red.500' : 'brand.500'}>
                  <BiImageAdd />
                </Icon>
              </InputRightElement>
            )}
            <Text fontSize='12px' color='grey.500'>
              {helpText}
            </Text>
            {error ? (
              <Text fontSize='14px' color='red.500'>
                {error}
              </Text>
            ) : null}
          </Flex>

          <Flex alignItems='center' gap={2}>
            {withDownload ? (
              <IconButton
                aria-label={t('components.image_upload.image_upload_download_button_label')}
                as={ForwardedFeatherIcon}
                variant='unstyled'
                name='download'
                size='18px'
                color={!innerRef.current?.files?.[0] ? 'gray.300' : 'brand.500'}
                cursor={!innerRef.current?.files?.[0] ? 'not-allowed' : 'pointer'}
                onClick={() => downloadFile(innerRef.current?.files?.[0], 'carteirinha-background')}
              />
            ) : null}

            {!disabled && (
              <IconButton
                aria-label={t('components.image_upload.image_upload_delete_button_label')}
                as={ForwardedFeatherIcon}
                variant='unstyled'
                name='trash-2'
                size='18px'
                color={!file ? 'gray.300' : 'red.500'}
                cursor={!file ? 'not-allowed' : 'pointer'}
                onClick={() => {
                  if (innerRef?.current) {
                    innerRef.current.value = '';
                    innerRef.current.files = null;
                  }

                  setFile(undefined);
                  onFileChange?.(undefined);
                }}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </InputGroup>
  );
});
